import type { SxProps } from '@mui/material';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { useTextFieldTokens } from '@ovComponents/2-component/textField/textField.tokens';
import { normalizeRichTextHtml } from '@utils/richTextHtml';
import { Box, IconButton, Typography, useTheme } from 'hephaestus/web';
import { Translate as TranslateIcon } from 'hephaestus/webIcons';
import Quill, { type QuillOptions } from 'quill';
import QuillImageResize from 'quill-resize-image';
import * as QuillTableUI from 'quill-table-ui';
import {
  type Dispatch,
  type ForwardedRef,
  forwardRef,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';

Quill.register({ 'modules/tableUI': QuillTableUI.default }, true);
Quill.register('modules/imageResize', QuillImageResize);

const syncQuillHtml = (quill: Quill, html: string) => {
  const delta = quill.clipboard.convert({ html: html || '' });
  quill.setContents(delta, 'silent');
};

export interface RichTextEditorArgs extends QuillOptions {
  value: any;
  setValue: Dispatch<SetStateAction<any>>;
  id?: string;
  sx?: SxProps;
  minHeight?: string | number;
  label?: string;
  fullToolbar?: boolean;
  type?: 'default' | 'multiLanguage';
  onTranslate?: () => void;
  testId?: string;
}

/**
 * Possible options for a Full Style Toolbar:
 *
 * const toolbarOptions = [
    [{ font: [] }, { size: [] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    ['table'],
    [{ script: 'super' }, { script: 'sub' }],
    [{ header: '1' }, { header: '2' }, 'blockquote', 'code-block'],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['direction', { align: [] }],
    ['link', 'image', 'video', 'formula'],
    ['clean'],
  ];
 */
const RichTextEditor = forwardRef(
  (props: RichTextEditorArgs, ref: ForwardedRef<any>) => {
    const [isFocused, setIsFocused] = useState(false);
    const toolbarOptions: any[] = [
      [{ header: [1, 2, 3, 4, 5, 6] }, { size: [] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote'],
      [
        { list: 'ordered' },
        { list: 'bullet' },
        { indent: '-1' },
        { indent: '+1' },
      ],
      [
        { align: '' },
        { align: 'center' },
        { align: 'right' },
        { align: 'justify' },
        { align: [] },
      ],
      ['link'],
    ];

    if (props.fullToolbar !== false) {
      toolbarOptions.push(['translate']);
      toolbarOptions.push(['image']);
      toolbarOptions.push(['clean']);
      toolbarOptions.push(['table']);
      toolbarOptions.push(['formula']);
      toolbarOptions.push([{ color: [] }, { background: [] }]);
      toolbarOptions.push([{ script: 'super' }, { script: 'sub' }]);
    }

    const theme = useTheme();
    const tokens = useTextFieldTokens(theme);
    const fontFamily = theme.font.base;

    const modules = {
      toolbar: toolbarOptions,
      table: true,
      tableUI: true,
      imageResize: true,
    };

    const containerRef = useRef(null);
    const quillRef = useRef<Quill | null>(null);
    const isFocusedRef = useRef(false);
    const setValueRef = useRef(props.setValue);
    setValueRef.current = props.setValue;

    useEffect(() => {
      const container: any = containerRef.current;
      const editorContainer = container?.appendChild(
        container?.ownerDocument.createElement('div'),
      );
      const boundsSelector = `#${CSS.escape(props.id ?? 'richTextEditor')}`;
      const quill = new Quill(editorContainer, {
        theme: 'snow',
        modules,
        bounds: boundsSelector,
      });

      quillRef.current = quill;

      if (ref) {
        (ref as any).current = quill;
      }

      const emitValue = () => {
        setValueRef.current(quill.root.innerHTML);
      };

      syncQuillHtml(quill, props.value ?? '');

      quill.on(Quill.events.TEXT_CHANGE, emitValue);

      const editor = container.querySelector('.ql-editor');
      const handleFocus = () => {
        isFocusedRef.current = true;
        setIsFocused(true);
      };
      const handleBlur = () => {
        isFocusedRef.current = false;
        setIsFocused(false);
      };

      if (editor) {
        editor.addEventListener('focus', handleFocus);
        editor.addEventListener('blur', handleBlur);
      }

      return () => {
        quillRef.current = null;
        if (editor) {
          editor.removeEventListener('focus', handleFocus);
          editor.removeEventListener('blur', handleBlur);
        }
        container.innerHTML = '';
      };
    }, []);

    useEffect(() => {
      const quill = quillRef.current;
      if (!quill || isFocusedRef.current) {
        return;
      }

      const nextValue = props.value ?? '';
      const currentValue = quill.root.innerHTML;
      if (
        normalizeRichTextHtml(currentValue) === normalizeRichTextHtml(nextValue)
      ) {
        return;
      }

      syncQuillHtml(quill, nextValue);
    }, [props.value]);

    return (
      <div>
        {props.label && (
          <Box component='label' sx={{ display: 'block', mb: '4px' }}>
            <Typography variant='labelSmall' sx={{ color: tokens.labelColor }}>
              {props.label}
            </Typography>
          </Box>
        )}
        <div
          id={props.id ?? 'richTextEditor'}
          ref={containerRef}
          data-testid={props.testId}
          style={{
            border: isFocused ? '2px solid #000000' : '1px solid #999999',
            borderRadius: '5px',
            position: 'relative',
            ...(props.sx as React.CSSProperties),
          }}
        >
          <style>
            {`
          .ql-container {
            border: none !important;
            min-height: ${props.minHeight || '100px'} !important;
          }
          .ql-toolbar {
            border: none !important;
            border-bottom: 1px solid #999999 !important;
          }
          .ql-editor {
            font-family: ${fontFamily};
          }
          .ql-editor p, .ql-editor td  {
            font-size: 14px;
            line-height: 20px;
            font-weight: 400;
            letter-spacing: 0.25px;
          }
          .ql-editor p .ql-size-small, .ql-editor td .ql-size-small {
            font-size: 12px;
            line-height: 16px;
            font-weight: 400;
            letter-spacing: 0.25px;
          }
          .ql-editor p .ql-size-large, .ql-editor td .ql-size-large {
            font-size: 16px;
            line-height: 24px;
            letter-spacing: 0.25px;
            font-weight: 500;
          }
          .ql-editor p .ql-size-huge, .ql-editor td .ql-size-huge {
            font-size: 20px;
            line-height: 32px;
            letter-spacing: 1px;
            font-weight: 500;
          }
          .ql-editor p strong, .ql-editor td strong {
            font-weight: 600 !important;
          }
          .ql-editor h1, .ql-editor h2, .ql-editor h3,
          .ql-editor h4, .ql-editor h5, .ql-editor h6 {
            font-weight: 600;
          }
          .ql-editor h1 strong, .ql-editor h2 strong, .ql-editor h3 strong,
          .ql-editor h4 strong, .ql-editor h5 strong, .ql-editor h6 strong {
            font-weight: 700 !important;
          }
        `}
          </style>
          {props.type === 'multiLanguage' && (
            <Box
              sx={{
                position: 'absolute',
                right: 5,
                top: 5,
                zIndex: 1,
              }}
            >
              <IconButton
                aria-label='translate'
                onClick={props.onTranslate}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                  size: 'small',
                  color: '#000000',
                }}
              >
                <Icon icon={TranslateIcon} size='small' />
              </IconButton>
            </Box>
          )}
        </div>
      </div>
    );
  },
);

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
